import { createSignal, createEffect, onCleanup } from 'solid-js';
import { styled } from 'solid-styled-components';
import {
  COLORS,
  SPACING,
  TYPOGRAPHY,
  BORDER_RADIUS,
  SHADOWS,
} from '../../../constants/design-tokens';
import { ERROR_MESSAGES } from '../../../constants/app-constants';
import Button from './Button';

// Styled components
const ErrorContainer = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${SPACING[8]};
  background: ${COLORS.background.primary};
  border: 1px solid ${COLORS.error[200]};
  border-radius: ${BORDER_RADIUS.lg};
  box-shadow: ${SHADOWS.md};
  text-align: center;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    min-height: 300px;
    padding: ${SPACING[6]};
    margin: ${SPACING[4]};
  }
`;

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${COLORS.error[50]};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${SPACING[6]};

  svg {
    width: 40px;
    height: 40px;
    color: ${COLORS.error[500]};
  }
`;

const ErrorTitle = styled.h2`
  font-size: ${TYPOGRAPHY.fontSize['2xl']};
  font-weight: ${TYPOGRAPHY.fontWeight.bold};
  color: ${COLORS.text.primary};
  margin: 0 0 ${SPACING[4]} 0;

  @media (max-width: 768px) {
    font-size: ${TYPOGRAPHY.fontSize.xl};
  }
`;

const ErrorMessage = styled.p`
  font-size: ${TYPOGRAPHY.fontSize.base};
  color: ${COLORS.text.secondary};
  line-height: ${TYPOGRAPHY.lineHeight.relaxed};
  margin: 0 0 ${SPACING[6]} 0;
  max-width: 400px;
`;

const ErrorDetails = styled.details`
  margin: ${SPACING[4]} 0;
  padding: ${SPACING[4]};
  background: ${COLORS.neutral[50]};
  border-radius: ${BORDER_RADIUS.md};
  border: 1px solid ${COLORS.neutral[200]};
  width: 100%;
  max-width: 500px;

  summary {
    cursor: pointer;
    font-weight: ${TYPOGRAPHY.fontWeight.medium};
    color: ${COLORS.text.secondary};
    margin-bottom: ${SPACING[2]};

    &:hover {
      color: ${COLORS.text.primary};
    }
  }

  pre {
    background: ${COLORS.neutral[100]};
    padding: ${SPACING[3]};
    border-radius: ${BORDER_RADIUS.base};
    overflow-x: auto;
    font-size: ${TYPOGRAPHY.fontSize.sm};
    color: ${COLORS.text.secondary};
    margin: ${SPACING[2]} 0 0 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${SPACING[3]};
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const AlertTriangleIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const RefreshIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="m3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const HomeIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9,22 9,12 15,12 15,22"></polyline>
  </svg>
);

/**
 * Professional Error Boundary component with user-friendly error display
 * Provides fallback UI when component errors occur with recovery options
 *
 * @param {Object} props - Component props
 * @param {JSX.Element} props.children - Child components to protect
 * @param {JSX.Element} props.fallback - Custom fallback component
 * @param {Function} props.onError - Error callback handler
 * @param {boolean} props.showDetails - Show error details in development
 */
const ErrorBoundary = (props) => {
  const {
    children,
    fallback,
    onError,
    showDetails = process.env.NODE_ENV === 'development',
  } = props;

  const [error, setError] = createSignal(null);
  const [errorInfo, setErrorInfo] = createSignal(null);

  // Global error handler for unhandled promise rejections
  const handleUnhandledRejection = (event) => {
    console.error('Unhandled promise rejection:', event.reason);

    setError({
      name: 'UnhandledPromiseRejection',
      message: event.reason?.message || 'An async operation failed',
      stack: event.reason?.stack || 'No stack trace available',
    });

    setErrorInfo({
      componentStack: 'Global promise rejection handler',
    });

    onError?.(event.reason, { componentStack: 'Global promise rejection handler' });
  };

  // Global error handler for JavaScript errors
  const handleGlobalError = (event) => {
    console.error('Global JavaScript error:', event.error);

    setError(
      event.error || {
        name: 'GlobalError',
        message: event.message || 'An unexpected error occurred',
        stack: 'No stack trace available',
      }
    );

    setErrorInfo({
      componentStack: `at ${event.filename}:${event.lineno}:${event.colno}`,
    });

    onError?.(event.error, {
      componentStack: `at ${event.filename}:${event.lineno}:${event.colno}`,
    });
  };

  // Set up global error handlers
  createEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('unhandledrejection', handleUnhandledRejection);
      window.addEventListener('error', handleGlobalError);

      onCleanup(() => {
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        window.removeEventListener('error', handleGlobalError);
      });
    }
  });

  const handleRetry = () => {
    setError(null);
    setErrorInfo(null);
    // Force a re-render by clearing the error state
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const reportError = () => {
    const errorReport = {
      error: error(),
      errorInfo: errorInfo(),
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    // In a real app, you'd send this to your error tracking service
    console.error('Error Report:', errorReport);

    // Copy to clipboard for easy reporting
    navigator.clipboard
      ?.writeText(JSON.stringify(errorReport, null, 2))
      .then(() => alert('Error details copied to clipboard'))
      .catch(() => console.log('Could not copy error details'));
  };

  // If there's an error, show the error boundary UI
  if (error()) {
    if (fallback) {
      return fallback;
    }

    return (
      <ErrorContainer role="alert" aria-live="assertive">
        <ErrorIcon>
          <AlertTriangleIcon />
        </ErrorIcon>

        <ErrorTitle>Oops! Something went wrong</ErrorTitle>

        <ErrorMessage>{error().message || ERROR_MESSAGES.genericError}</ErrorMessage>

        {showDetails && (
          <ErrorDetails>
            <summary>Technical Details</summary>
            <div>
              <strong>Error:</strong> {error().name || 'Unknown Error'}
            </div>
            {error().stack && <pre>{error().stack}</pre>}
            {errorInfo()?.componentStack && (
              <div>
                <strong>Component Stack:</strong>
                <pre>{errorInfo().componentStack}</pre>
              </div>
            )}
          </ErrorDetails>
        )}

        <ActionButtons>
          <Button variant="primary" onClick={handleRetry} leftIcon={<RefreshIcon />}>
            Try Again
          </Button>

          <Button variant="secondary" onClick={handleGoHome} leftIcon={<HomeIcon />}>
            Go Home
          </Button>

          {showDetails && (
            <Button variant="ghost" onClick={reportError} size="sm">
              Copy Error Details
            </Button>
          )}
        </ActionButtons>
      </ErrorContainer>
    );
  }

  // If no error, render children normally
  return children;
};

/**
 * Higher-order component to wrap components with error boundary
 * @param {Function} Component - Component to wrap
 * @returns {Function} Wrapped component with error boundary
 */
export const withErrorBoundary = (Component) => {
  return (props) => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
